import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import apiClient from "../services/apiClient";
import { authService } from "../services/authService";
import "./AttendanceKioskPage.css";

// 학원 출결 화면(2026-09-30). 학원에 둔 폰이나 태블릿에 교사 계정으로 열어 둔다.
// 1) 아이가 부모님 번호 뒤 8자리 → 2) 형제면 이름 고르기 → 끝. 얼굴 사진에 동의한 집만 3) 사진 → 보내기.
// 등원·하원은 오늘 기록으로 정한다(처음이면 등원, 등원 뒤면 하원). 하원이면 몇 시간 있었는지도 알린다.
// 이은 부모님께 사진과 함께 텔레그램으로 보낸다(api/echo/attendance_views.py). 사진은 저장하지 않는다.
// 학원 태블릿에는 /attendance/<열쇠> 를 띄운다. 로그인 없이 열려서 지인에듀 로그인이 풀려도 멈추지 않는다.
// /attendance(열쇠 없음)는 로그인한 교사 계정으로 연다.
const DIGIT_COUNT = 8;
const RESULT_SECONDS = 5;
const KEY_LIST = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "지우기", "0", "확인"];

const STEP_KEYPAD = "keypad";
const STEP_CHOOSE = "choose";
const STEP_PHOTO = "photo";
const STEP_RESULT = "result";

const AttendanceKioskPage = () => {
  const { kioskKey } = useParams();
  // 열쇠 주소면 로그인 없이, 아니면 교사 계정으로 부른다
  const apiBase = kioskKey ? `/api/v1/echo/attendance-kiosk/${kioskKey}` : "/api/v1/echo/teacher/attendance";
  const apiOption = { withAuth: !kioskKey };
  const [academyName, setAcademyName] = useState("");
  const [step, setStep] = useState(STEP_KEYPAD);
  const [digits, setDigits] = useState("");
  const [choiceList, setChoiceList] = useState([]);
  const [student, setStudent] = useState(null);
  const [kind, setKind] = useState("in");
  const [photo, setPhoto] = useState("");
  const [cameraState, setCameraState] = useState("idle"); // idle | on | denied
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [isBusy, setIsBusy] = useState(false);
  const [now, setNow] = useState(new Date());
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const resetTimerRef = useRef(null);

  useEffect(() => {
    if (kioskKey) {
      apiClient
        .get(apiBase, apiOption)
        .then((response) => setAcademyName(response.data.academy_name || ""))
        .catch((caught) => setError(caught.response?.data?.error || ""));
    } else if (!authService.isAuthenticated()) {
      window.location.assign("/login");
      return undefined;
    } else {
      apiClient
        .get("/api/v1/user/me", { withAuth: true })
        .then((response) => setAcademyName(response.data.organization_name || response.data.name || ""))
        .catch(() => {});
    }
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kioskKey]);

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  };

  const reset = useCallback(() => {
    clearTimeout(resetTimerRef.current);
    stopCamera();
    setStep(STEP_KEYPAD);
    setDigits("");
    setChoiceList([]);
    setStudent(null);
    setPhoto("");
    setResult(null);
    setError("");
  }, []);

  useEffect(() => () => stopCamera(), []);

  // 사진 단계에서만 카메라를 켠다(앞 카메라). 허락하지 않으면 사진 없이 보낸다.
  useEffect(() => {
    if (step !== STEP_PHOTO || photo) return;
    setCameraState("idle");
    navigator.mediaDevices
      ?.getUserMedia({ video: { facingMode: "user" }, audio: false })
      .then((media) => {
        streamRef.current = media;
        if (videoRef.current) videoRef.current.srcObject = media;
        setCameraState("on");
      })
      .catch(() => setCameraState("denied"));
    if (!navigator.mediaDevices) setCameraState("denied");
  }, [step, photo]);

  // 번호를 누르면 바로 끝낸다. 등원·하원은 오늘 기록으로 정한다(처음이면 등원, 등원 뒤면 하원).
  // 얼굴 사진에 동의한 집만 사진 화면으로 가고, 아니면 그 자리에서 보낸다(echo/consent.py)
  const goNext = (chosen) => {
    const chosenKind = chosen.suggested_kind || "in";
    setStudent(chosen);
    setKind(chosenKind);
    setChoiceList([]);
    if (chosen.face_photo_allowed) {
      setStep(STEP_PHOTO);
      return;
    }
    submit(chosenKind, chosen);
  };

  const lookup = async (value) => {
    setError("");
    setIsBusy(true);
    try {
      const response = await apiClient.post(`${apiBase}/lookup`, { digits: value }, apiOption);
      const list = response.data.student_list || [];
      if (list.length === 1) goNext(list[0]);
      else {
        setChoiceList(list);
        setStep(STEP_CHOOSE);
      }
    } catch (caught) {
      setError(caught.response?.data?.error || "다시 눌러 주세요.");
      setDigits("");
    } finally {
      setIsBusy(false);
    }
  };

  const press = (key) => {
    if (isBusy) return;
    if (key === "지우기") {
      setDigits((prev) => prev.slice(0, -1));
      setError("");
      return;
    }
    if (key === "확인") {
      if (digits.length === DIGIT_COUNT) lookup(digits);
      else setError("번호 8자리를 눌러 주세요.");
      return;
    }
    const next = (digits + key).slice(0, DIGIT_COUNT);
    setDigits(next);
    if (next.length === DIGIT_COUNT) lookup(next);
  };

  // 키보드로도 누를 수 있게(번호 단계에서만)
  useEffect(() => {
    if (step !== STEP_KEYPAD) return undefined;
    const onKey = (event) => {
      if (/^[0-9]$/.test(event.key)) press(event.key);
      else if (event.key === "Backspace") press("지우기");
      else if (event.key === "Enter") press("확인");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const shoot = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement("canvas");
    // 너무 큰 사진은 줄인다(텔레그램으로 빨리 가게)
    const scale = Math.min(1, 960 / video.videoWidth);
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);
    const context = canvas.getContext("2d");
    // 화면에 거울처럼 보인 방향 그대로 보낸다
    context.translate(canvas.width, 0);
    context.scale(-1, 1);
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    setPhoto(canvas.toDataURL("image/jpeg", 0.82));
    stopCamera();
  };

  const submit = async (chosenKind = kind, chosenStudent = student) => {
    if (!chosenStudent) return;
    setIsBusy(true);
    try {
      const response = await apiClient.post(
        `${apiBase}/check`,
        { student_id: chosenStudent.id, kind: chosenKind, photo: photo || null },
        apiOption
      );
      setResult(response.data);
      setStep(STEP_RESULT);
      resetTimerRef.current = setTimeout(reset, RESULT_SECONDS * 1000);
    } catch (caught) {
      setError(caught.response?.data?.error || "보내지 못했어요. 다시 눌러 주세요.");
    } finally {
      setIsBusy(false);
    }
  };

  const timeText = now.toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" });
  const dateText = now.toLocaleDateString("ko-KR", { month: "long", day: "numeric", weekday: "short" });

  return (
    <div className="kiosk">
      <header className="kiosk__head">
        <div className="kiosk__brand">
          <span className="kiosk__dot" />
          <span className="kiosk__academy">{academyName}</span>
          <span className="kiosk__pill">ECHO 출결</span>
        </div>
        <div className="kiosk__clock kiosk__clock--small">
          <b>{timeText}</b>
          <span>{dateText}</span>
        </div>
      </header>

      <main className="kiosk__body">
        {/* 태블릿 가로에서만 보이는 왼쪽 인사 칸 */}
        <aside className="kiosk__hello">
          <p className="kiosk__eyebrow">ATTENDANCE</p>
          <h1>
            안녕! 👋
            <br />
            <span className="kiosk__hl">번호를 눌러</span>
            <br />
            출석해요
          </h1>
          <div className="kiosk__clock kiosk__clock--big">
            <b>{timeText}</b>
            <span>{dateText}</span>
          </div>
        </aside>
        {step === STEP_KEYPAD && (
          <div className="kiosk__panel">
            <p className="kiosk__guide">부모님 번호 <b>뒤 8자리</b>를 눌러요</p>
            <div className="kiosk__digits">
              {Array.from({ length: DIGIT_COUNT }).map((_, index) => (
                <span key={index} className={index === 4 ? "kiosk__gap" : ""}>{digits[index] || ""}</span>
              ))}
            </div>
            <p className="kiosk__error">{error}</p>
            <div className="kiosk__keys">
              {KEY_LIST.map((key) => (
                <button
                  key={key}
                  type="button"
                  className={key.length > 1 ? `kiosk__key--word${key === "확인" ? " kiosk__key--ok" : ""}` : ""}
                  disabled={isBusy}
                  onClick={() => press(key)}
                >
                  {key}
                </button>
              ))}
            </div>
          </div>
        )}

        {step === STEP_CHOOSE && (
          <div className="kiosk__panel kiosk__choice">
            <p className="kiosk__guide">누구예요?</p>
            {choiceList.map((one) => (
              <button key={one.id} type="button" onClick={() => goNext(one)}>{one.name}</button>
            ))}
            <button type="button" className="kiosk__back" onClick={reset}>처음으로</button>
          </div>
        )}

        {step === STEP_PHOTO && (
          <div className="kiosk__panel kiosk__photo">
            <p className="kiosk__guide"><b>{student?.name}</b>, 얼굴이 보이게 찍어요 📸</p>
            <div className="kiosk__frame">
              {photo ? (
                <img src={photo} alt="찍은 사진" />
              ) : cameraState === "denied" ? (
                <p className="kiosk__nocam">카메라를 쓸 수 없어요.<br />사진 없이 보낼 수 있어요.</p>
              ) : (
                <video ref={videoRef} autoPlay playsInline muted />
              )}
            </div>
            <p className="kiosk__error">{error}</p>
            {photo ? (
              <div className="kiosk__row">
                <button type="button" className="kiosk__secondary" disabled={isBusy} onClick={() => setPhoto("")}>다시 찍기</button>
                <button type="button" className="kiosk__primary" disabled={isBusy} onClick={() => submit()}>
                  {isBusy ? "보내는 중..." : "보내기"}
                </button>
              </div>
            ) : cameraState === "denied" ? (
              <button type="button" className="kiosk__primary" disabled={isBusy} onClick={() => submit()}>사진 없이 보내기</button>
            ) : (
              <button type="button" className="kiosk__shutter" disabled={cameraState !== "on"} onClick={shoot} aria-label="찍기">
                <span />
              </button>
            )}
            <button type="button" className="kiosk__back" onClick={reset}>처음으로</button>
          </div>
        )}

        {step === STEP_RESULT && result && (
          <div className={`kiosk__panel kiosk__result kiosk__result--${result.kind}`} onClick={reset}>
            <div className="kiosk__emoji">{result.kind === "in" ? "👋" : "🏠"}</div>
            <b>{result.name}</b>
            <p className="kiosk__result-main">
              {result.kind === "in" ? "어서 와요! 등원했어요" : "수고했어요! 하원했어요"}
              {result.time ? ` · ${result.time}` : ""}
            </p>
            {result.kind === "out" && result.stay_text && (
              <p className="kiosk__stay">오늘 학원에 <b>{result.stay_text}</b> 있었어요 ⏱</p>
            )}
            <small>
              {result.repeated
                ? "방금 찍었어요"
                : result.telegram_sent
                ? "부모님께 알려 드렸어요 📲"
                : result.connected
                ? "알림을 보내지 못했어요"
                : "출결을 적었어요"}
            </small>
          </div>
        )}
      </main>
    </div>
  );
};

export default AttendanceKioskPage;
